import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  CircularProgress,
  Box,
  Divider,
  Paper,
  TextField,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import BlogCreate from "./BlogCreate";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { BlogPost } from "../utils/mediaHelpers";

// Extend BlogPost interface locally to include hidden property
interface BlogPostWithHidden extends BlogPost {
  hidden?: boolean;
}

function BlogList() {
  const [posts, setPosts] = useState<BlogPostWithHidden[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  // Category filtering
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [availableCategories] = useState<string[]>([
    "Project",
    "WhatsApp",
    "News",
    "Secret",
  ]);

  const fetchPosts = async (category?: string) => {
    try {
      setLoading(true);
      const headers: Record<string, string> = {};
      if (user?.token) {
        headers.Authorization = `Bearer ${user.token}`;
      }

      // Build URL with category filter
      let url = "/api/blog/";
      if (category && category !== "All") {
        url += `?category=${encodeURIComponent(category)}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error("Failed to fetch blog posts");
      }
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Category filter handler
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    fetchPosts(category);
  };

  useEffect(() => {
    fetchPosts(selectedCategory);
    // eslint-disable-next-line
  }, []);

  const handleEdit = (post: BlogPostWithHidden) => {
    setEditingId(post.id || null);
    setEditTitle(post.title || "");
    setEditContent(post.content || "");
  };

  const handleSave = async (id: string) => {
    await fetch(`/api/blog/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
      },
      body: JSON.stringify({ title: editTitle, content: editContent }),
    });
    setEditingId(null);
    fetchPosts();
  };

  const handleDelete = async (id: string) => {
    await fetch(`/api/blog/${id}`, {
      method: "DELETE",
      headers: {
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
      },
    });
    setPosts(posts.filter((p) => p.id !== id));
  };

  const handleToggleHide = async (id: string, currentlyHidden: boolean) => {
    const endpoint = `/api/blog/${id}/${currentlyHidden ? "unhide" : "hide"}`;
    await fetch(endpoint, {
      method: "POST",
      headers: {
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        "Content-Type": "application/json",
      },
    });
    fetchPosts();
  };

  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {user && (user.role === "admin" || user.role === "blog") && (
        <BlogCreate onSuccess={() => fetchPosts(selectedCategory)} />
      )}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ mb: 4 }}
      >
        {t("blog.title") || "Blog"}
      </Typography>

      {/* Category Filter */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            label="Filter by Category"
          >
            <MenuItem value="All">All Categories</MenuItem>
            {availableCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedCategory !== "All" && (
          <Chip
            label={`Showing: ${selectedCategory}`}
            onDelete={() => handleCategoryChange("All")}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      {posts.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {t("blog.no_posts") || "No blog posts available yet."}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={4}>
          {posts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Box
                sx={{ cursor: "pointer" }}
                tabIndex={0}
                role="button"
                aria-label={`View blog post: ${post.title}`}
                onClick={() => navigate(`/blog/${post.id}`)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    navigate(`/blog/${post.id}`);
                }}
              >
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s ease-in-out",
                    opacity: post.hidden ? 0.5 : 1,
                    pointerEvents: post.hidden ? "none" : "auto",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardHeader
                    title={
                      <Box display="flex" alignItems="center">
                        {editingId === post.id ? (
                          <TextField
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            fullWidth
                            size="small"
                          />
                        ) : (
                          <Typography variant="h5" component="h2" gutterBottom>
                            {post.title}
                          </Typography>
                        )}
                        {/* Show (Hidden) label for admins */}
                        {user?.role === "admin" && post.hidden && (
                          <Typography
                            color="warning.main"
                            variant="caption"
                            sx={{ ml: 2 }}
                          >
                            (Hidden)
                          </Typography>
                        )}
                      </Box>
                    }
                    subheader={
                      <Typography variant="body2" color="text.secondary">
                        {format(
                          new Date(post.created_at || ""),
                          "MMMM d, yyyy"
                        )}
                      </Typography>
                    }
                    action={
                      user &&
                      user.role === "admin" &&
                      (editingId === post.id ? (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSave(post.id || "");
                            }}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingId(null);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(post);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(post.id || "");
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <IconButton
                            color={post.hidden ? "success" : "warning"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleHide(post.id || "", !!post.hidden);
                            }}
                          >
                            {post.hidden ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </Stack>
                      ))
                    }
                  />
                  <Divider />
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Category Chip */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={post.category || "News"}
                        size="small"
                        color={
                          post.category === "Project"
                            ? "primary"
                            : post.category === "WhatsApp"
                            ? "secondary"
                            : post.category === "Secret"
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </Box>
                    {editingId === post.id ? (
                      <TextField
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        sx={{ mb: 2 }}
                      />
                    ) : null}
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default BlogList;
